import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/layout/Sidebar";
import DataTable, { TableColumn } from "../components/ui/DataTable";
import AddNewButton from "../components/ui/AddNewButton";
import Modal from "../components/layout/Modal";
import DeleteModal from "../components/layout/DeleteModal";
import DietaryForm, { DietaryFormValues } from "../components/dietary/DietaryForm";
import { useTags, useUpdateTag, useDeleteTag } from "../services/tags";
import { toast } from "../hooks/use-toast";
import EmptyState from "../components/ui/EmptyState";
import TablePagination from "../components/ui/TablePagination";
import ROUTES from "../utils/ROUTES";
import { Pencil, Trash2 } from "lucide-react";
import axios from "axios";

const getErrorMessage = (err: unknown): string =>
  axios.isAxiosError(err) ? err.response?.data?.error ?? err.message : "Something went wrong. Please try again.";

interface DietaryTag {
  id: string;
  name: string;
}

const DietaryPreference = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingTag, setEditingTag] = useState<DietaryTag | null>(null);
  const [deletingTag, setDeletingTag] = useState<DietaryTag | null>(null);

  const { data: tagsData, isLoading, isError } = useTags(currentPage);
  const tags = tagsData?.tags;
  const pagination = tagsData?.pagination;
  const updateTag = useUpdateTag();
  const deleteTag = useDeleteTag();

  const handleConfirmDelete = () => {
    if (!deletingTag) return;
    deleteTag.mutate(deletingTag.id, { onSuccess: () => setDeletingTag(null) });
  };

  const handleSave = (values: DietaryFormValues) => {
    if (!values.id) return;
    updateTag.mutate(
      { id: values.id, body: { name: values.name } },
      {
        onSuccess: () => { toast({ variant: "success", title: "Dietary tag updated", description: "The dietary tag has been successfully updated." }); setEditingTag(null); },
        onError: (err) => { toast({ variant: "error", title: "Failed to update dietary tag", description: getErrorMessage(err) }); },
      }
    );
  };

  const columns: TableColumn<DietaryTag>[] = [
    {
      key: "name",
      header: "Tag Name",
      width: "70%",
      cellClassName: "px-6 py-[22px]",
      render: (row) => (
        <span className="text-[16px] font-bold text-content-heading">
          {row.name}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      headerAlign: "right",
      width: "180px",
      cellClassName: "px-6 py-4",
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <div className="relative group">
            <button
              onClick={() => setEditingTag(row)}
              className="p-[6px] hover:opacity-70 transition-opacity"
              aria-label="Edit tag"
            >
              <Pencil className="w-[15px] h-[15px]" />
            </button>
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-[4px] bg-content-heading px-2 py-1 text-[11px] text-white shadow-md z-20">Edit tag</span>
          </div>
          <div className="relative group">
            <button
              onClick={() => setDeletingTag(row)}
              className="p-[6px] hover:opacity-70 transition-opacity"
              aria-label="Delete tag"
            >
              <Trash2 className="w-[13px] h-[15px]" />
            </button>
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-[4px] bg-content-heading px-2 py-1 text-[11px] text-white shadow-md z-20">Delete tag</span>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-surface-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="flex-1 min-w-0 overflow-y-auto px-4 sm:px-8 lg:pl-11 lg:pr-8 pt-6 lg:pt-[60px] pb-16">
        <div className="flex items-center mb-4 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-black/5 transition-colors"
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="#012926" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-[28px] sm:text-[34px] uppercase tracking-[0.68px] leading-none text-brand-dark"
              style={{ fontFamily: '"Bebas Neue", sans-serif' }}
            >
              Dietary Preference
            </h1>
            <p className="mt-3 text-[14px] sm:text-[16px] font-medium text-content-label">
              Manage dietary tags applied to menu items and customer profiles.
            </p>
          </div>

          <AddNewButton onClick={() => navigate(ROUTES.ADD_DIETARY)} />
        </div>

        <div className="mt-8 lg:mt-[36px]">
          {isLoading && (
            <p className="text-[14px] text-content-label">Loading…</p>
          )}
          {isError && (
            <p className="text-[14px] text-content-error">Failed to load tags.</p>
          )}
          {!isLoading && !isError && (
            !tags?.length
              ? <EmptyState message="No dietary tags found." />
              : (
                  <>
                    <DataTable columns={columns} rows={tags!} rowKey={(t) => t.id} />
                    <TablePagination currentPage={currentPage} pagination={pagination} rowCount={tags!.length} onPageChange={setCurrentPage} />
                  </>
                )
          )}
        </div>
      </main>

      <Modal
        isOpen={editingTag !== null}
        onClose={() => setEditingTag(null)}
        title={`Edit "${editingTag?.name ?? ""}"`}
      >
        <DietaryForm
          initialValues={{ id: editingTag?.id, name: editingTag?.name }}
          onSave={handleSave}
        />
      </Modal>

      <DeleteModal
        isOpen={deletingTag !== null}
        onClose={() => setDeletingTag(null)}
        onConfirm={handleConfirmDelete}
        title="Are you sure you want to delete this tag?"
        message={
          <>
            This action will permanently remove the{" "}
            <span
              className="font-semibold text-content-body"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              &ldquo;{deletingTag?.name}&rdquo;
            </span>{" "}
            dietary tag from your system. This action cannot be undone.
          </>
        }
        confirmLabel="Delete Tag"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default DietaryPreference;
